import { useEffect, useState } from "react";

import { Loader2, TrendingUp, Users, Clock, Target, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { supabase } from "@/integrations/supabase/client";

interface OnboardingStats {
  total_users: number;
  auto_redirected_users: number;
  quick_start_users: number;
  prebuilt_path_users: number;
  custom_path_users: number;
  braindump_path_users: number;
  completed_board_users: number;
  created_project_users: number;
  started_session_users: number;
  avg_seconds_to_path_selection: number;
  avg_seconds_to_board_creation: number;
  avg_seconds_to_first_project: number;
  avg_seconds_to_first_session: number;
  avg_seconds_to_first_value: number;
}

const OnboardingAnalytics = () => {
  const navigate = useNavigate();
  
  // Enable keyboard shortcuts for back navigation
  useKeyboardNavigation({
    onBack: () => navigate("/dashboard"),
  });
  
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<OnboardingStats | null>(null);
  const [daysBack, setDaysBack] = useState(30);

  useEffect(() => {
    fetchStats();
  }, [daysBack]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("get_onboarding_stats", {
        days_back: daysBack,
      });

      if (error) throw error;
      if (data && data.length > 0) {
        setStats(data[0]);
      }
    } catch (error) {
      console.error("Error fetching onboarding stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number | null) => {
    if (!seconds) return "N/A";
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    if (minutes === 0) return `${remainingSeconds}s`;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const formatPercentage = (value: number, total: number) => {
    if (!total) return "0%";
    return `${((value / total) * 100).toFixed(1)}%`;
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>No Data Available</CardTitle>
            <CardDescription>No onboarding data found for the selected period.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Onboarding Analytics</h1>
            <p className="text-muted-foreground">
              Track user behavior and optimize the onboarding flow
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant={daysBack === 7 ? "default" : "outline"}
            onClick={() => setDaysBack(7)}
          >
            7 Days
          </Button>
          <Button
            variant={daysBack === 30 ? "default" : "outline"}
            onClick={() => setDaysBack(30)}
          >
            30 Days
          </Button>
          <Button
            variant={daysBack === 90 ? "default" : "outline"}
            onClick={() => setDaysBack(90)}
          >
            90 Days
          </Button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total_users}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed Boards</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.completed_board_users}</div>
            <p className="text-xs text-muted-foreground">
              {formatPercentage(stats.completed_board_users, stats.total_users)} conversion
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Started Sessions</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.started_session_users}</div>
            <p className="text-xs text-muted-foreground">
              {formatPercentage(stats.started_session_users, stats.total_users)} activation
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Time to First Value</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatTime(stats.avg_seconds_to_first_value)}
            </div>
            <p className="text-xs text-muted-foreground">Avg time to first session</p>
          </CardContent>
        </Card>
      </div>

      {/* Onboarding Paths */}
      <Card>
        <CardHeader>
          <CardTitle>Onboarding Path Distribution</CardTitle>
          <CardDescription>
            How users are discovering and starting their journey
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Auto-Redirected to Board Creation</p>
              <p className="text-sm text-muted-foreground">
                Users with no content were automatically redirected
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">{stats.auto_redirected_users}</p>
              <p className="text-sm text-muted-foreground">
                {formatPercentage(stats.auto_redirected_users, stats.total_users)}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Viewed Quick Start Guide</p>
              <p className="text-sm text-muted-foreground">
                Users who clicked "Not ready yet?"
              </p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">{stats.quick_start_users}</p>
              <p className="text-sm text-muted-foreground">
                {formatPercentage(stats.quick_start_users, stats.total_users)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Board Creation Paths */}
      <Card>
        <CardHeader>
          <CardTitle>Board Creation Path Preferences</CardTitle>
          <CardDescription>Which path do users choose when creating boards?</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Pre-built Boards</p>
              <p className="text-sm text-muted-foreground">Start with a template</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">{stats.prebuilt_path_users}</p>
              <p className="text-sm text-muted-foreground">
                {formatPercentage(
                  stats.prebuilt_path_users,
                  stats.prebuilt_path_users +
                    stats.custom_path_users +
                    stats.braindump_path_users
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Custom Guided Build</p>
              <p className="text-sm text-muted-foreground">Answer questions to build board</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">{stats.custom_path_users}</p>
              <p className="text-sm text-muted-foreground">
                {formatPercentage(
                  stats.custom_path_users,
                  stats.prebuilt_path_users +
                    stats.custom_path_users +
                    stats.braindump_path_users
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Brain Dump / Not Sure</p>
              <p className="text-sm text-muted-foreground">Free-form exploration</p>
            </div>
            <div className="text-right">
              <p className="text-2xl font-bold">{stats.braindump_path_users}</p>
              <p className="text-sm text-muted-foreground">
                {formatPercentage(
                  stats.braindump_path_users,
                  stats.prebuilt_path_users +
                    stats.custom_path_users +
                    stats.braindump_path_users
                )}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Timing Metrics */}
      <Card>
        <CardHeader>
          <CardTitle>Time to Key Milestones</CardTitle>
          <CardDescription>
            Average time users take to reach important onboarding milestones
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="font-medium">Time to Select Path</p>
            <p className="text-lg font-bold">
              {formatTime(stats.avg_seconds_to_path_selection)}
            </p>
          </div>

          <div className="flex items-center justify-between">
            <p className="font-medium">Time to Create Board</p>
            <p className="text-lg font-bold">
              {formatTime(stats.avg_seconds_to_board_creation)}
            </p>
          </div>

          <div className="flex items-center justify-between">
            <p className="font-medium">Time from Board to First Project</p>
            <p className="text-lg font-bold">
              {formatTime(stats.avg_seconds_to_first_project)}
            </p>
          </div>

          <div className="flex items-center justify-between">
            <p className="font-medium">Time from Board to First Session</p>
            <p className="text-lg font-bold">
              {formatTime(stats.avg_seconds_to_first_session)}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Funnel Metrics */}
      <Card>
        <CardHeader>
          <CardTitle>Onboarding Funnel</CardTitle>
          <CardDescription>User progression through onboarding steps</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p>Total Users</p>
              <p className="font-bold">{stats.total_users}</p>
            </div>
            <div className="w-full bg-secondary h-2 rounded-full">
              <div className="bg-primary h-2 rounded-full" style={{ width: "100%" }} />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p>Completed Board Creation</p>
              <p className="font-bold">
                {stats.completed_board_users} (
                {formatPercentage(stats.completed_board_users, stats.total_users)})
              </p>
            </div>
            <div className="w-full bg-secondary h-2 rounded-full">
              <div
                className="bg-primary h-2 rounded-full"
                style={{
                  width: `${(stats.completed_board_users / stats.total_users) * 100}%`,
                }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p>Created First Project</p>
              <p className="font-bold">
                {stats.created_project_users} (
                {formatPercentage(stats.created_project_users, stats.total_users)})
              </p>
            </div>
            <div className="w-full bg-secondary h-2 rounded-full">
              <div
                className="bg-primary h-2 rounded-full"
                style={{
                  width: `${(stats.created_project_users / stats.total_users) * 100}%`,
                }}
              />
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p>Started First Session</p>
              <p className="font-bold">
                {stats.started_session_users} (
                {formatPercentage(stats.started_session_users, stats.total_users)})
              </p>
            </div>
            <div className="w-full bg-secondary h-2 rounded-full">
              <div
                className="bg-primary h-2 rounded-full"
                style={{
                  width: `${(stats.started_session_users / stats.total_users) * 100}%`,
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingAnalytics;
